import React from 'react';
import { User } from '../../../types/userManagement';

interface UserListProps {
  users: User[];
  loading: boolean;
  onEdit: (user: User) => void;
  onView: (user: User) => void;
  onDelete: (userId: string) => void;
  onToggleStatus: (userId: string, isActive: boolean) => void;
  onUnlock: (userId: string) => void;
}

const UserList: React.FC<UserListProps> = ({
  users,
  loading,
  onEdit,
  onView,
  onDelete,
  onToggleStatus,
  onUnlock
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  const formatRoles = (roles: string[]) => {
    return roles.join(', ') || 'Nessun ruolo';
  };

  const isUserLocked = (user: User) => {
    return user.lockoutEnd && new Date(user.lockoutEnd) > new Date();
  };

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  if (users.length === 0) {
    return (
      <div className="no-users">
        <p>Nessun utente trovato con i criteri di ricerca specificati.</p>
      </div>
    );
  }

  return (
    <div className="user-list">
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Nome Completo</th>
              <th>Ruoli</th>
              <th>Stato</th>
              <th>Ultimo Accesso</th>
              <th>Data Creazione</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.isAttivo ? 'inactive-user' : ''}>
                <td>
                  <div className="user-email">
                    {user.email}
                    {!user.emailConfirmed && (
                      <span className="email-not-confirmed" title="Email non confermata">
                        ⚠️
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="user-name">
                    {user.nomeCompleto || 'N/A'}
                    {user.avatar && (
                      <img 
                        src={user.avatar} 
                        alt="Avatar" 
                        className="user-avatar-small"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </td>
                <td>
                  <div className="user-roles">
                    {user.roles.map(role => (
                      <span key={role} className={`role-badge role-${role.toLowerCase()}`}>
                        {role}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="user-status">
                    <span className={`status-badge ${user.isAttivo ? 'active' : 'inactive'}`}>
                      {user.isAttivo ? 'Attivo' : 'Inattivo'}
                    </span>
                    {isUserLocked(user) && (
                      <span className="status-badge locked" title={`Bloccato fino al ${formatDate(user.lockoutEnd!)}`}>
                        Bloccato
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {user.dataUltimoAccesso ? formatDate(user.dataUltimoAccesso) : 'Mai'}
                </td>
                <td>
                  {formatDate(user.dataCreazione)}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => onView(user)}
                      title="Visualizza dettagli"
                    >
                      👁️
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => onEdit(user)}
                      title="Modifica utente"
                    >
                      ✏️
                    </button>
                    <button
                      className={`btn btn-sm ${user.isAttivo ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => onToggleStatus(user.id, user.isAttivo)}
                      title={user.isAttivo ? 'Disattiva utente' : 'Attiva utente'}
                    >
                      {user.isAttivo ? '⏸️' : '▶️'}
                    </button>
                    {isUserLocked(user) && (
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => onUnlock(user.id)}
                        title="Sblocca utente"
                      >
                        🔓
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => onDelete(user.id)}
                      title="Elimina utente"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserList;